const { Observable } = require('rxjs');
const flatMap = require('lodash/flatMap');
const flatten = require('lodash/flatten');
const without = require('lodash/without');
const uniq = require('lodash/uniq');
const uniqBy = require('lodash/uniqBy');
const invertBy = require('lodash/invertBy');
const reverse = require('lodash/reverse');
const fromPairs = require('lodash/fromPairs');
const partition = require('lodash/partition');
const memoize = require('lodash/memoize');

const {
  isRef,
  isError,
  parseRef,
  error,
  normalise,
  expand,
  isInvalidate,
  isPromise,
  getValues,
  watchValues,
  RouteMatcher,
  MusterError,
  PENDING,
} = require('../../muster-utils');

/**
 * Muster App Instance
 *
 * The muster app instance is used to query the Muster data graph using get(), set() and call()
 * operations.
 */
class Router {
  /**
   * @param {Map<RoutePattern, RouteHandler>} routes Key/value map that maps route patterns to their
   * respective handlers
   * @param {Object} options Configuration options
   * @param {Store} options.store Graph data store
   * @param {Object} options.log Log object
   * @param {Object} options.log.debug Debug-level log function
   * @param {Object} options.log.info Info-level log function
   * @param {Object} options.log.warn Warning-level log function
   * @param {Object} options.log.error Error-level log function
   * @param {number} [options.keepAlive] Number of milliseconds to retain values in store after
   * unsubscribing from value stream
   */
  constructor(routes, {
    store,
    log,
    keepAlive,
  } = {}) {
    this.routes = new RouteMatcher(routes);
    this.activeHandlerStreams = new Map();
    this.log = log;
    this.store = store;
    this.keepAlive = keepAlive || 0;

    this.match = memoize((path) => this.routes.get(path));

    this.routes.keys().forEach((routePath) => {
      this.log.debug('ROUTE', routePath);
    });
  }

  /**
   * Get paths from the graph
   * @param {PathSet|Array<PathSet>} pathSet Path(s) to fetch from the graph
   * @returns {Observable<Array<PathValue>>} Combined stream that emits corresponding graph values
   * @throws If no route handler matches the specified paths
   */
  get(pathSet) {
    this.log.info('GET', pathSet);

    // Expand the queries into a single array of static paths
    const queries = Array.isArray(pathSet) ? pathSet : [pathSet];
    const expandedPaths = flatMap(queries, (query) => expand(query));

    // Map unique paths to their corresponding route handlers
    const pathHandlers = getPathHandlers(uniq(expandedPaths), this.match);

    // Group the paths into subsets based on their corresponding route handlers
    const handlerPathSets = groupPathHandlersByHandler(pathHandlers);

    // Create a filtered output stream that emits any updates that match the queries
    const stream = this.store.listen(expandedPaths, { keepAlive: this.keepAlive });

    // Subscribe to the relevant route handlers and retain teardown callbacks
    const teardownCallbacks = handlerPathSets.map(({ handler, paths: handlerPaths }) => {
      const methodHandler = handler.get;
      if (!methodHandler) {
        const handlerPath = getHandlerPath(handler, this.routes);
        throw new Error(`Handler at ${handlerPath} does not specify a get handler`);
      }

      const handlerStream = methodHandler(handlerPaths, {
        get: this.get.bind(this),
        watchValues: (paths) => watchValues(this.get(paths)),
      });
      handlerPaths.forEach((path) => {
        registerPathHandlerStream(this.activeHandlerStreams, path, handlerStream);
      });
      const subscription = followGetHandlerRedirects(handlerStream, { get: this.get.bind(this) })
        .subscribe({
          next: (pathValues) => {
            this.store.set(pathValues);
          },
        });
      return () => {
        handlerPaths.forEach((path) => {
          unregisterPathHandlerStream(this.activeHandlerStreams, path, handlerStream);
        });
        subscription.unsubscribe();
      };
    });

    // Return a filtered stream that unsubscribes from route handlers when it is disposed
    return stream.finally(() => {
      teardownCallbacks.forEach((unsubscribe) => { unsubscribe(); });
    });

    function registerPathHandlerStream(dictionary, path, handlerStream) {
      const existingHandlerStreams = dictionary.has(path) ? dictionary.get(path) : [];
      const updatedHandlerStreams = existingHandlerStreams.concat(handlerStream);
      dictionary.set(path, updatedHandlerStreams);
    }

    function unregisterPathHandlerStream(dictionary, path, handlerStream) {
      const existingHandlerStreams = dictionary.get(path);
      const updatedHandlerStreams = without(existingHandlerStreams, handlerStream);
      if (updatedHandlerStreams.length > 0) {
        dictionary.set(path, updatedHandlerStreams);
      } else {
        dictionary.delete(path);
      }
    }
  }

  /**
   * Update paths in the graph
   * @param {PathValue|Array<PathValue>} pathValueSet Path/value pairs to update
   * @returns {Promise<Array<PathValue>>} Array of updated path/value pairs
   */
  set(pathValueSet) {
    /* eslint-disable */
    // Allow arguments to be passed in as (path, value)
    if ((arguments.length === 2) && (typeof arguments[0] === 'string')) {
      pathValueSet = { path: arguments[0], value: arguments[1] };
    }
    /* eslint-enable */
    this.log.info('SET', pathValueSet);
    const pathValues = Array.isArray(pathValueSet) ? pathValueSet : [pathValueSet];

    // Filter out duplicate path values, choosing the latest value in the case of duplicates
    const filteredPathValues = reverse(uniqBy(reverse(pathValues), ({ path }) => path));
    const valuesByPath = fromPairs(
      filteredPathValues.map(({ path, value }) => [path, value])
    );

    // Map paths to their corresponding route handlers
    const filteredPaths = filteredPathValues.map(({ path }) => path);
    const pathHandlers = getPathHandlers(filteredPaths, this.match);

    // Group the paths into subsets based on their corresponding path handlers
    const handlerPathSets = groupPathHandlersByHandler(pathHandlers);

    // Set the values and return the merged results
    return Promise.all(
      handlerPathSets.map(({ handler, paths: handlerPaths }) => {
        const methodHandler = handler.set;
        if (!methodHandler) {
          const handlerPath = getHandlerPath(handler, this.routes);
          throw new Error(`Handler at ${handlerPath} does not specify a set handler`);
        }
        const handlerPathValues = handlerPaths.map((path) => ({ path, value: valuesByPath[path] }));
        try {
          const response = maybePromise(
            methodHandler(handlerPathValues, {
              get: this.get.bind(this),
              set: this.set.bind(this),
              getValues: (paths) => getValues(this.get(paths)),
            })
          )
          .then((responsePathValues) => {
            const [redirectPathValues, updatedPathValues] = partition(responsePathValues,
              ({ value }) => isRef(value)
            );
            this.store.set(updatedPathValues);
            if (redirectPathValues.length === 0) { return updatedPathValues; }

            return maybePromise(followSetHandlerRedirects(redirectPathValues, valuesByPath, {
              set: this.set.bind(this),
            }))
            .then((resolvedPathValues) => {
              this.store.set(resolvedPathValues);
              return updatedPathValues.concat(resolvedPathValues);
            });
          });
          if (!isPromise(response)) { return response; }
          return response.catch((e) => handlerPaths.map((path) => ({ path, value: error(e) })));
        } catch (e) {
          return handlerPaths.map((path) => ({ path, value: error(e) }));
        }
      })
    )
    .then((handlerPathValues) => Promise.all(flatten(handlerPathValues)))
    .then((responsePathValues) => {
      const pathErrors = responsePathValues.filter(({ value }) => isError(value));
      if (pathErrors.length > 0) {
        const errorMessage = uniq(pathErrors.map(({ value }) => value.value.message)).join('\n');
        throw new MusterError(errorMessage, {
          code: MusterError.HANDLER_ERROR,
          path: pathValues,
          method: 'set',
          results: responsePathValues,
          errors: pathErrors.map(({ value }) => new MusterError(value.value.message, value.value)),
        });
      }
      return responsePathValues;
    });
  }

  /**
   * Call a function on the graph
   * @param {Path} path Path to the graph function
   * @param {...any} args Arguments to pass to the function
   * @returns {Promise<any>} Value returned by the call
   */
  call(path, ...args) {
    this.log.info('CALL', path, args);
    const { handler } = getPathHandlers([path], this.match)[0];
    if (!handler.call) {
      throw new Error(`Handler at ${path} does not specify a call handler`);
    }
    try {
      return Promise.resolve(
        maybePromise(handler.call(path, args, {
          get: this.get.bind(this),
          getValues: (paths) => getValues(this.get(paths)),
          set: this.set.bind(this),
          call: this.call.bind(this),
        }))
        .then((response) => {
          if (response.values) {
            const pathValues = response.values;
            const invalidatedPathValues = pathValues.filter(({ value }) => isInvalidate(value));
            const invalidatedPaths = invalidatedPathValues.map((pathValue) => pathValue.path);
            if (invalidatedPaths.length > 0) {
              invalidatePaths(invalidatedPaths, this.activeHandlerStreams);
            }
            const updatedPathValues = pathValues.filter(({ value }) => !isInvalidate(value));
            if (updatedPathValues.length > 0) {
              this.store.set(updatedPathValues);
            }
          }
          return response.response;
        })
      );
    } catch (e) {
      return Promise.reject(e);
    }
  }
}

module.exports = Router;

function getPathHandlers(paths, match) {
  // Takes a path string and a match function
  // Returns an array of `{ path, handler }` objects
  return paths.map((path) => {
    const handler = match(path);
    if (!handler) {
      throw new Error(`Handler not found for path ${path}`);
    }
    return { path, handler };
  });
}

function groupPathHandlersByHandler(pathHandlers) {
  // Takes an array of `{ path, handler }` objects
  // Returns an array of `{ handler, paths: ['path1', 'path2', ..., 'pathN'] }` objects
  const handlerPathsMap = pathHandlers.reduce((pathsMap, { path, handler }) => {
    const existingHandlerPaths = pathsMap.get(handler) || [];
    return pathsMap.set(handler, existingHandlerPaths.concat(path));
  }, new Map());
  return [...handlerPathsMap.entries()].map(([handler, paths]) => ({ handler, paths }));
}

function getHandlerPath(routeHandler, routeMatcher) {
  return routeMatcher.entries()
    .map(([pattern, value]) => ({ pattern, handler: value }))
    .find(({ handler }) => (handler === routeHandler))
    .pattern;
}

function invalidatePaths(invalidatedPaths, activeHandlerStreams) {
  const invalidatedStreamPaths = getPathsByStream(invalidatedPaths, activeHandlerStreams);
  [...invalidatedStreamPaths.entries()].forEach(([stream, paths]) => {
    stream.reload(paths);
  });
}

function getPathsByStream(updatedPaths, streamsMap) {
  return [...streamsMap.entries()]
    .filter(([path]) => updatedPaths.includes(path))
    .reduce((pathsByStream, [path, streams]) => {
      streams.forEach((stream) => {
        pathsByStream.set(stream, (pathsByStream.get(stream) || []).concat(path));
      });
      return pathsByStream;
    },
    new Map()
  );
}

function followGetHandlerRedirects(handlerStream, { get }) {
  // Keep a cache of currently-active redirects to prevent unnecessary re-fetching
  let activeRedirects = [];
  let activeRedirectsStream = Observable.never();

  // Create a stream that processes any redirects and fetches their values
  const responseStream = handlerStream.switchMap((pathValues) => {
    // If we're already listening to this exact set of redirects, return the existing redirect
    // stream, otherwise return a new stream containing the correct set of redirects
    const updatedRedirects = getUpdatedRedirects(activeRedirects, pathValues);
    const redirectsHaveChanged = !areSortedPathValuesEqual(activeRedirects, updatedRedirects);
    const updatedRedirectStream = (redirectsHaveChanged ?
      getUpdatedRedirectStream(updatedRedirects, { get }) :
      activeRedirectsStream
    );

    // Retain the list of currently active redirects for potential future use
    activeRedirects = updatedRedirects;
    activeRedirectsStream = updatedRedirectStream;

    // Get a list of path value updates, with redirects swapped out with pending atoms
    const initialValues = pathValues.map((pathValue) => (
      isRef(pathValue.value) ? { path: pathValue.path, value: PENDING } : pathValue)
    );

    // Return the redirect stream along with the non-redirected values
    return updatedRedirectStream.startWith(initialValues);
  });

  // Ensure the output stream proxies the handler stream's `reload()` method
  responseStream.reload = (updatedPaths) => handlerStream.reload(updatedPaths);
  return responseStream;
}

function getUpdatedRedirects(existingRedirects, updatedPathValues) {
  // Create a key/value object that maps the updated redirects as { path: value }
  const updatedPathSet = fromPairs(updatedPathValues.map(({ path, value }) => [path, value]));

  // Get the set of previously-existing redirects that are unaffected by the update
  const remainingRedirects = existingRedirects.filter(({ path }) => !(path in updatedPathSet));

  // Get a list of { path, target } objects for the updated values that are redirects
  const updatedRedirects = updatedPathValues
    .filter(({ value }) => isRef(value))
    .map(({ path, value }) => ({ path, value: normalise(parseRef(value)) }));

  // Get a combined sorted list of all the still-active redirects plus any new ones
  const combinedRedirects = remainingRedirects.concat(updatedRedirects).sort(
    ({ path: path1 }, { path: path2 }) => (path1 > path2 ? 1 : -1)
  );
  return combinedRedirects;
}

function getUpdatedRedirectStream(redirects, { get }) {
  // If there are no redirects to fetch, return an empty observable that never completes
  if (redirects.length === 0) { return Observable.never(); }

  // Create key/value object that maps redirect source paths to target paths
  const sourceTargetMappings = fromPairs(redirects.map(
    ({ path, value }) => [path, value]
  ));
  // Create a reverse mapping that maps redirect target paths to source path arrays
  // (bearing in mind that each target path can map to multiple source paths)
  const targetSourceMappings = invertBy(sourceTargetMappings);

  // Fetch the target paths and map the results to their corresponding source paths
  const targetPaths = redirects.map(({ value }) => value);
  return get(targetPaths)
    .map((targetPathValues) =>
      flatMap(targetPathValues, ({ path, value }) => {
        const sourcePaths = targetSourceMappings[path];
        return sourcePaths.map((sourcePath) => ({ path: sourcePath, value }));
      })
    );
}

function followSetHandlerRedirects(targetPathValues, valuesByPath, { set }) {
  const redirects = targetPathValues
    .map(({ path, value }) => ({
      path,
      target: parseRef(value),
      value: valuesByPath[path],
    }));
  const redirectPathValues = redirects.map(({ target, value }) => ({ path: target, value }));
  return set(redirectPathValues)
    .then((updatedTargetPathValues) => {
      const sourceTargetMappings = fromPairs(redirects.map(({ path, target }) => [path, target]));
      const targetSourceMappings = invertBy(sourceTargetMappings);
      const resolvedPathValues = flatMap(updatedTargetPathValues, ({ path, value }) =>
        targetSourceMappings[path].map((sourcePath) => ({ path: sourcePath, value }))
      );
      return resolvedPathValues;
    });
}

function areSortedPathValuesEqual(pathValues1, pathValues2) {
  return (pathValues1.length === pathValues2.length) && pathValues1.every(
    ({ path: path1, value: value1 }, index) => {
      const { path: path2, value: value2 } = pathValues2[index];
      return (path1 === path2) && (value2 === value1);
    }
  );
}

function maybePromise(value) {
  if (isPromise(value)) {
    return value;
  }
  return {
    then: (callback) => callback(value),
  };
}
